import type { ReactNode } from 'react';
import { Fragment } from 'react';
import styled from 'styled-components';

import { Link } from '@sorare/routing';

import { Vertical } from '#src/atoms/layout/flex/index.tsx';
import { BodyM, BodyS } from '#src/atoms/typography/index.tsx';
import { Country } from '#src/components/seo/Player/Infos/Country/index.tsx';
import { PlayerAvatar } from '#src/components/seo/PlayerAvatar/index.tsx';
import { Row } from '#src/components/seo/ui.tsx';
import type { Unit } from '#src/lib/style.ts';

import { squadGridTemplateColumns } from '../gridTemplateColumns.ts';

const StyledBodyS = styled(BodyS)`
  display: flex;
  gap: var(--half-unit);
`;
const OverflowHiddenVertical = styled(Vertical)`
  overflow: hidden;
`;
const EllipsisBodyM = styled(BodyM)`
  text-overflow: ellipsis;
  overflow: hidden;
  white-space: nowrap;
`;

export type Props<T extends string> = {
  stats: T[];
  statWidths?: Partial<Record<T, Unit>>;
  player: {
    slug: string;
    displayName: string;
    pictureUrl: string | null;
    age: number;
    to: string;
    country: {
      code: string;
      threeLetterCode: string | null;
    };
    stats: Record<T, ReactNode>;
  };
};

export const SquadPlayer = <T extends string>({
  player,
  stats,
  statWidths,
}: Props<T>) => {
  return (
    <Row
      style={{
        gridTemplateColumns: squadGridTemplateColumns(stats, statWidths),
      }}
    >
      <Link to={player.to}>
        <PlayerAvatar src={player.pictureUrl} alt={player.displayName} />
      </Link>
      <OverflowHiddenVertical gap={0}>
        <EllipsisBodyM as="p">
          <Link to={player.to}> {player.displayName}</Link>
        </EllipsisBodyM>
        <StyledBodyS as="p" color="var(--c-nd-600)">
          <span>{player.age}</span>
          •
          <Country size={1.5} {...player.country} />
        </StyledBodyS>
      </OverflowHiddenVertical>
      {stats.map(stat => (
        <Fragment key={stat}>
          {['string', 'number'].includes(typeof player.stats[stat]) ? (
            <BodyM as="p">{player.stats[stat]}</BodyM>
          ) : (
            player.stats[stat]
          )}
        </Fragment>
      ))}
    </Row>
  );
};
